import { useEffect, useRef, useState } from "react";
import { IconChevronDown, IconWorld } from "@tabler/icons-react";
import { useIntl } from "react-intl";
import { useLang, useSetLanguage } from "../translations/i18n";

const OPTIONS = [
	{ code: "es", short: "ES", nameId: "COMMON.LANG_NAME_ES" },
	{ code: "en", short: "EN", nameId: "COMMON.LANG_NAME_EN" },
];

/**
 * Desplegable de idioma (arriba a la derecha): icono + ES/EN + chevron.
 */
export default function LanguageSwitcher({ style } = {}) {
	const intl = useIntl();
	const lang = useLang();
	const setLanguage = useSetLanguage();
	const active = lang === "en" ? "en" : "es";
	const [open, setOpen] = useState(false);
	const rootRef = useRef(null);

	const activeOption = OPTIONS.find((o) => o.code === active) || OPTIONS[0];

	useEffect(() => {
		if (!open) return undefined;
		const onDoc = (e) => {
			if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
		};
		const onKey = (e) => {
			if (e.key === "Escape") setOpen(false);
		};
		document.addEventListener("mousedown", onDoc);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onDoc);
			document.removeEventListener("keydown", onKey);
		};
	}, [open]);

	return (
		<div ref={rootRef} style={{ position: "relative", ...style }}>
			<button
				type="button"
				aria-haspopup="listbox"
				aria-expanded={open}
				aria-label={intl.formatMessage({ id: "COMMON.LANGUAGE" })}
				onClick={() => setOpen((v) => !v)}
				style={{
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					gap: 8,
					height: 44,
					padding: "0 12px",
					borderRadius: 12,
					border: "1px solid rgba(15, 23, 42, 0.10)",
					background: "rgba(255, 255, 255, 0.92)",
					boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
					cursor: "pointer",
					fontFamily: '"League Spartan", system-ui, -apple-system, sans-serif',
					color: "#0f172a",
					appearance: "none",
				}}
			>
				<span
					style={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						height: 20,
						lineHeight: 0,
					}}
				>
					<IconWorld size={20} stroke={1.75} color="#5b3df0" aria-hidden />
				</span>
				<span
					style={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						height: 20,
						fontSize: 15,
						fontWeight: 700,
						letterSpacing: "0.04em",
						lineHeight: 1,
					}}
				>
					{activeOption.short}
				</span>
				<span
					style={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						height: 20,
						lineHeight: 0,
					}}
				>
					<IconChevronDown
						size={18}
						stroke={2}
						color="#64748b"
						aria-hidden
						style={{
							transform: open ? "rotate(180deg)" : "none",
							transition: "transform 160ms ease",
						}}
					/>
				</span>
			</button>

			{open ? (
				<ul
					role="listbox"
					aria-label={intl.formatMessage({ id: "COMMON.LANGUAGE" })}
					style={{
						position: "absolute",
						top: "calc(100% + 8px)",
						right: 0,
						zIndex: 50,
						minWidth: "100%",
						margin: 0,
						padding: 6,
						listStyle: "none",
						borderRadius: 12,
						border: "1px solid rgba(15, 23, 42, 0.10)",
						background: "#fff",
						boxShadow: "0 18px 48px rgba(15, 23, 42, 0.14)",
					}}
				>
					{OPTIONS.map((opt) => {
						const selected = opt.code === active;
						return (
							<li key={opt.code} role="option" aria-selected={selected}>
								<button
									type="button"
									onClick={() => {
										setOpen(false);
										if (!selected) setLanguage(opt.code);
									}}
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										width: "100%",
										padding: "12px 14px",
										border: "none",
										borderRadius: 8,
										background: selected ? "rgba(91, 61, 240, 0.08)" : "transparent",
										color: selected ? "#5b3df0" : "#0f172a",
										cursor: "pointer",
										fontFamily: "inherit",
										fontSize: 15,
										fontWeight: selected ? 700 : 600,
										letterSpacing: "0.04em",
										lineHeight: 1,
										textAlign: "center",
									}}
									onMouseEnter={(e) => {
										if (!selected) e.currentTarget.style.background = "rgba(15, 23, 42, 0.04)";
									}}
									onMouseLeave={(e) => {
										e.currentTarget.style.background = selected
											? "rgba(91, 61, 240, 0.08)"
											: "transparent";
									}}
								>
									{opt.short}
								</button>
							</li>
						);
					})}
				</ul>
			) : null}
		</div>
	);
}
